/**
 * Is the friendly Mailroom on for me? (10/28 — pick C.)
 *
 * Two sources, one answer:
 *   - the office's switch (GET /api/mailbox/view): `forced` when the person is
 *     on the pilot list (Regina, Darcy) or the switch is on for everyone;
 *   - otherwise the person's own "Try the new Mailroom" preference
 *     (`mailroom_v2_view`).
 * `forced` also hides "Back to the old Mailroom".
 *
 * The Mailroom page, the toolbar's Inbox chip and anything else that changes
 * with the friendly view read it from here, so they always agree.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useUserPreference } from "./user-preference";

export const FRIENDLY_PREF_KEY = "mailroom_v2_view";

export interface FriendlyView {
    forced: boolean;
    everyone: boolean;
}

/** Pure: the office switch wins; otherwise the person's own pick. */
export function friendlyOn(view: FriendlyView | undefined, pref: boolean): boolean {
    return (view?.forced ?? false) || pref;
}

export function useFriendlyMailroom(options?: {
    enabled?: boolean;
}): [boolean, (on: boolean) => void, { loaded: boolean; forced: boolean; everyone: boolean }] {
    const enabled = options?.enabled ?? true;
    const [pref, setPref, { loaded: prefLoaded }] = useUserPreference<boolean>(FRIENDLY_PREF_KEY, false, { enabled });
    const view = useQuery({
        queryKey: ["mailbox", "view"],
        enabled,
        staleTime: 5 * 60 * 1000,
        queryFn: () => api.get<FriendlyView>("/api/mailbox/view"),
    });
    const loaded = !enabled || (prefLoaded && (view.isFetched || view.isError));
    return [
        friendlyOn(view.data, pref),
        setPref,
        { loaded, forced: view.data?.forced ?? false, everyone: view.data?.everyone ?? false },
    ];
}
